import { useEffect, useRef, useState } from 'react';

import TextLink from '@/components/TextLink';
import logo from '@/shared/assets/images/relaxi-logo.svg?url';

import { getDocumentVersionUrl, getRegistrationObligations } from '../api';
import {
  clearRegistrationLegalState,
  getRegistrationLegalState,
  registrationObligationsExpired,
  setRegistrationLegalState,
} from '../session';
import type { RelaxiRegistrationObligations, RelaxiRequirement } from '../types';

import styles from './index.module.scss';

type Props = { readonly onContinue: (sessionId: string) => void | Promise<void> };

/** Relaxi-only step. It never calls or alters Logto authentication APIs. */
const RegistrationAgreementPage = ({ onContinue }: Props) => {
  const [obligations, setObligations] = useState<RelaxiRegistrationObligations | undefined>(
    () => getRegistrationLegalState().obligations
  );
  const [accepted, setAccepted] = useState<string[]>(
    () => getRegistrationLegalState().accepted ?? []
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submitting = useRef(false);
  const revision = useRef(0);

  const load = async () => {
    // eslint-disable-next-line @silverhand/fp/no-mutation -- discard responses from superseded loads
    const current = ++revision.current;
    setBusy(true);
    setObligations(undefined);
    setAccepted([]);
    try {
      clearRegistrationLegalState();
      const result = await getRegistrationObligations();
      if (current === revision.current) {
        setObligations(result);
        if (result.requirements.length === 0) {
          setRegistrationLegalState({ obligations: result, accepted: [], reviewed: true });
          await onContinue(result.registrationSessionId);
        }
      }
    } catch {
      if (current === revision.current) {
        setError('Не удалось загрузить документы. Попробуйте ещё раз.');
      }
    } finally {
      if (current === revision.current) {
        setBusy(false);
      }
    }
  };

  useEffect(() => {
    if (!getRegistrationLegalState().obligations) {
      void load();
    }
    return () => {
      // eslint-disable-next-line @silverhand/fp/no-mutation -- ignore pending network responses on unmount
      revision.current += 1;
    };
  }, []);

  const required =
    obligations?.requirements.filter(
      ({ required, displayMode }) => required && displayMode !== 'notice_only'
    ) ?? [];
  const optional =
    obligations?.requirements.filter(
      ({ required, displayMode }) => !required && displayMode !== 'notice_only'
    ) ?? [];
  const notices =
    obligations?.requirements.filter(({ displayMode }) => displayMode === 'notice_only') ?? [];
  const canContinue =
    Boolean(obligations) && !busy && required.every(({ id }) => accepted.includes(id));

  const submit = async () => {
    if (!obligations || !canContinue || submitting.current) {
      return;
    }
    // eslint-disable-next-line @silverhand/fp/no-mutation -- synchronous guard against duplicate submissions
    submitting.current = true;
    setBusy(true);
    setError('');
    try {
      if (registrationObligationsExpired(obligations)) {
        throw new Error('expired');
      }
      setRegistrationLegalState({ obligations, accepted, reviewed: true });
      await onContinue(obligations.registrationSessionId);
    } catch {
      setError(
        'Не удалось сохранить выбор или срок сессии истёк. Проверьте обновлённые документы и повторите.'
      );
      await load();
    } finally {
      // eslint-disable-next-line @silverhand/fp/no-mutation -- release the submission lock after settlement
      submitting.current = false;
      setBusy(false);
    }
  };

  const items = (requirements: RelaxiRequirement[]) =>
    requirements.map((item) => (
      <div key={item.id} className={styles.item}>
        <label className={styles.check}>
          <input
            type="checkbox"
            disabled={busy}
            checked={accepted.includes(item.id)}
            aria-label={item.document.title}
            onChange={({ target }) => {
              const next = target.checked
                ? [...accepted, item.id]
                : accepted.filter((id) => id !== item.id);
              setAccepted(next);
              if (obligations) {
                setRegistrationLegalState({ obligations, accepted: next, reviewed: false });
              }
            }}
          />
        </label>
        <a
          href={getDocumentVersionUrl(item.version.id, obligations?.locale)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {item.document.title}
        </a>
      </div>
    ));

  return (
    <section
      data-relaxi-registration
      className={styles.card}
      aria-labelledby="relaxi-registration-title"
    >
      <img className={styles.logo} src={logo} alt="Релакси" />
      <h1 id="relaxi-registration-title">Добро пожаловать в Релакси</h1>
      <p className={styles.subtitle}>Создайте свой аккаунт</p>
      <p className={styles.intro}>
        Ознакомьтесь с документами и подтвердите согласие перед регистрацией.
      </p>
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      {busy && !obligations && <p role="status">Загружаем документы…</p>}
      {required.length > 0 && (
        <div className={styles.group}>
          <h2>Для регистрации</h2>
          {items(required)}
        </div>
      )}
      {notices.map((item) => (
        <p key={item.id} className={styles.notice}>
          <a
            href={getDocumentVersionUrl(item.version.id, obligations?.locale)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.document.title}
          </a>
          <span>Для ознакомления</span>
        </p>
      ))}
      {optional.length > 0 && (
        <div className={styles.optional}>
          {items(optional)}
          <p>Необязательно. Можно продолжить без этого согласия.</p>
        </div>
      )}
      {!busy && !obligations ? (
        <button
          type="button"
          className={styles.continueButton}
          onClick={() => {
            setError('');
            if (!getRegistrationLegalState().obligations) {
              void load();
            }
          }}
        >
          Повторить загрузку
        </button>
      ) : (
        <button
          type="button"
          className={styles.continueButton}
          disabled={!canContinue}
          onClick={() => {
            void submit();
          }}
        >
          {busy ? 'Сохраняем…' : 'Продолжить'}
        </button>
      )}
      {obligations && !canContinue && !busy && (
        <p className={styles.hint}>Подтвердите обязательные документы, чтобы продолжить.</p>
      )}
      <p className={styles.hint}>
        Уже есть аккаунт? <TextLink replace to="/sign-in" text="action.sign_in" />
      </p>
    </section>
  );
};

export default RegistrationAgreementPage;
